import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../../../hooks/useCart';

interface ShopifyProduct {
  id: string;
  name: string;
  category: string;
  price: number;
  image: string;
  special: string;
  variantId: string;
  availableForSale: boolean;
}

export default function SignaturePieces() {
  const [isVisible, setIsVisible] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [products, setProducts] = useState<ShopifyProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [addedIds, setAddedIds] = useState<Set<string>>(new Set());
  const [cardWidth, setCardWidth] = useState(320);
  const sectionRef = useRef<HTMLDivElement>(null);
  const { addItem } = useCart();
  const navigate = useNavigate();

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setIsVisible(true); },
      { threshold: 0.2 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const updateCardWidth = () => {
      if (window.innerWidth < 640) setCardWidth(Math.min(window.innerWidth - 48, 300));
      else if (window.innerWidth < 1024) setCardWidth(340);
      else setCardWidth(400);
    };
    updateCardWidth();
    window.addEventListener('resize', updateCardWidth);
    return () => window.removeEventListener('resize', updateCardWidth);
  }, []);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const res = await fetch(
          `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/shopify-signature-pieces`,
          { headers: { apikey: import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY } }
        );
        const data = await res.json();
        if (data.products) setProducts(data.products);
      } catch (err) {
        console.error('Failed to fetch signature pieces:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  const gap = 24;
  const maxIndex = Math.max(0, products.length - 1);

  const handlePrev = () => setCurrentIndex((prev) => (prev > 0 ? prev - 1 : maxIndex));
  const handleNext = () => setCurrentIndex((prev) => (prev < maxIndex ? prev + 1 : 0));

  const handleCardClick = (productId: string) => {
    const numericId = productId.split('/').pop();
    navigate(`/product/${numericId}`);
  };

  const handleAddToCart = (product: ShopifyProduct) => {
    if (!product.variantId || !product.availableForSale) return;
    addItem({ id: product.id, variantId: product.variantId, name: product.name, price: product.price, image: product.image });
    setAddedIds((prev) => new Set(prev).add(product.variantId));
    setTimeout(() => {
      setAddedIds((prev) => { const next = new Set(prev); next.delete(product.variantId); return next; });
    }, 1500);
  };

  return (
    <section id="signature" ref={sectionRef} className="relative w-full bg-black py-16 sm:py-24 md:py-32 overflow-hidden">
      <div className="w-[90%] max-w-[1200px] mx-auto">
        <div className={`text-center mb-12 sm:mb-16 md:mb-24 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <h2
            className="text-4xl sm:text-5xl md:text-6xl lg:text-8xl font-bold bg-gradient-to-r from-[#c0c0c0] via-white to-[#c0c0c0] bg-clip-text text-transparent"
            style={{ textShadow: '0 0 40px rgba(255, 255, 255, 0.3)', backgroundSize: '200% auto', animation: 'shimmerTitle 3s ease-in-out infinite' }}
          >
            Signature Collection
          </h2>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-[400px]">
            <div className="w-12 h-12 border-4 border-[#00d4ff]/30 border-t-[#00d4ff] rounded-full animate-spin"></div>
          </div>
        ) : (
          <div className="relative">
            {/* Nav Buttons */}
            {products.length > 1 && (
              <div className="flex justify-center gap-4 mb-6 md:hidden">
                <button onClick={handlePrev} className="w-12 h-12 flex items-center justify-center rounded-full bg-white/10 text-white hover:bg-[#00d4ff] transition-all duration-300 cursor-pointer">
                  <i className="ri-arrow-left-line text-xl"></i>
                </button>
                <button onClick={handleNext} className="w-12 h-12 flex items-center justify-center rounded-full bg-white/10 text-white hover:bg-[#00d4ff] transition-all duration-300 cursor-pointer">
                  <i className="ri-arrow-right-line text-xl"></i>
                </button>
              </div>
            )}

            {products.length > 1 && (
              <>
                <button onClick={handlePrev} className="hidden md:flex absolute left-0 top-1/2 -translate-y-1/2 -translate-x-16 z-30 w-14 h-14 items-center justify-center rounded-full bg-white/10 backdrop-blur-sm text-white transition-all duration-300 hover:bg-[#00d4ff] hover:scale-110 cursor-pointer">
                  <i className="ri-arrow-left-line text-2xl"></i>
                </button>
                <button onClick={handleNext} className="hidden md:flex absolute right-0 top-1/2 -translate-y-1/2 translate-x-16 z-30 w-14 h-14 items-center justify-center rounded-full bg-white/10 backdrop-blur-sm text-white transition-all duration-300 hover:bg-[#00d4ff] hover:scale-110 cursor-pointer">
                  <i className="ri-arrow-right-line text-2xl"></i>
                </button>
              </>
            )}

            <div className="overflow-hidden">
              <div
                className="flex transition-transform duration-700 ease-out"
                style={{ gap: `${gap}px`, transform: `translateX(-${currentIndex * (cardWidth + gap)}px)` }}
              >
                {products.map((product, index) => (
                  <div
                    key={product.id}
                    className="group relative flex-shrink-0 bg-black rounded-2xl border-2 border-[#00d4ff]/60 overflow-hidden transition-all duration-500 hover:border-[#00d4ff] hover:shadow-[0_0_60px_rgba(0,212,255,0.6)] cursor-pointer"
                    style={{
                      width: `${cardWidth}px`,
                      height: `${Math.round(cardWidth * 1.5)}px`,
                      opacity: index === currentIndex ? 1 : 0.5,
                      transform: index === currentIndex ? 'scale(1)' : 'scale(0.95)',
                    }}
                    onClick={() => handleCardClick(product.id)}
                    data-product-shop
                  >
                    <div className="relative w-full h-[65%] overflow-hidden">
                      <img src={product.image} alt={product.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                    </div>
                    <div className="h-[35%] flex flex-col items-center justify-center space-y-2 px-4">
                      <h3 className="text-white text-lg sm:text-xl md:text-2xl font-bold text-center">{product.name}</h3>
                      <p className="text-[#00d4ff] text-xs sm:text-sm italic text-center" style={{ textShadow: '0 0 10px rgba(0, 212, 255, 0.5)' }}>{product.special}</p>
                      <p className="text-white text-base sm:text-xl font-medium">${product.price.toFixed(2)}</p>
                      <button
                        onClick={(e) => { e.stopPropagation(); handleAddToCart(product); }}
                        disabled={!product.availableForSale}
                        className={`px-6 py-2 rounded-full text-xs font-bold tracking-widest uppercase transition-all duration-300 cursor-pointer whitespace-nowrap ${
                          addedIds.has(product.variantId) ? 'bg-green-400 text-black'
                          : product.availableForSale ? 'bg-[#00d4ff] text-black hover:bg-white hover:scale-105'
                          : 'bg-gray-700 text-gray-500 cursor-not-allowed'
                        }`}
                        style={product.availableForSale ? { boxShadow: '0 0 20px rgba(0,212,255,0.4)' } : {}}
                      >
                        {addedIds.has(product.variantId) ? '✓ Added!' : product.availableForSale ? 'Add to Cart' : 'Sold Out'}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Dots */}
            {products.length > 1 && (
              <div className="flex justify-center gap-2 mt-8">
                {products.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setCurrentIndex(i)}
                    className={`rounded-full transition-all duration-300 cursor-pointer ${i === currentIndex ? 'w-6 h-2 bg-[#00d4ff]' : 'w-2 h-2 bg-white/30 hover:bg-white/60'}`}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      <style>{`
        @keyframes shimmerTitle { 0%, 100% { background-position: 0% 50%; } 50% { background-position: 100% 50%; } }
      `}</style>
    </section>
  );
}
